import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import useDebounceState from '@/hooks/useDebounceState';
import useEntitySet from '@/hooks/useEntitySet';
import { RoleStatusEnum } from '@/types/schema';

import useStaffRolesWithRoleProfile from '../../views/StaffRoleManager/hooks/useStaffRolesWithRoleProfile';
import ListItem from './ListItem';

interface AddStaffRolesModalProps {
  contentName: string;
  staffRoleIds: string[];
  onChange: (nextRoleIds: string[]) => void;
  onClose: () => void;
}

function AddStaffRolesModal(props: AddStaffRolesModalProps) {
  const { t } = useTranslation('StaffRoles');
  const [searchText, setSearchText, debounceSearchText] = useDebounceState('');
  const { contentName, staffRoleIds, onChange, onClose } = props;
  const { selectedItemsSet, toggle } = useEntitySet(staffRoleIds);
  const { staffRoles, initialFetching, fetchMore } = useStaffRolesWithRoleProfile({
    filters: {
      searchText: debounceSearchText,
      status: [RoleStatusEnum.Active],
    },
  });

  const onSubmit = useCallback(() => {
    onChange(Array.from(selectedItemsSet));
  }, [onChange, selectedItemsSet]);

  const renderContent = useCallback(() => {
    if (initialFetching) {
      return <Loader />;
    }

    if (!staffRoles.length) {
      if (searchText) {
        return (
          <EmptyState
            className="flex-1"
            description={t('StaffRoles:Modal.SelectStaffRolesEmptyDescription', {
              text: searchText,
            })}
            title={t('StaffRoles:Modal.SelectStaffRolesEmptyTitle')}
          />
        );
      }
      return (
        <EmptyState
          className="flex-1"
          description={t('StaffRoles:Modal.NoStaffRolesDescription')}
          title={t('StaffRoles:Modal.NoStaffRolesAvailable')}
        />
      );
    }

    if (staffRoles.length) {
      return (
        <ContentListModal.Body
          className="flex-1"
          itemRenderer={(index: number) => {
            const staffRole = staffRoles[index];

            return (
              <ListItem
                key={staffRole.id}
                selected={selectedItemsSet.has(staffRole.id)}
                staffRole={staffRole}
                onSelect={toggle}
              />
            );
          }}
        />
      );
    }

    return null;
  }, [initialFetching, searchText, selectedItemsSet, staffRoles, t, toggle]);

  return (
    <ContentListModal.Main
      action={{
        discardText: t('Common:Cancel'),
        onSubmit,
        text: t('StaffRoles:Modal.AddStaffRoles'),
      }}
      fetchMore={fetchMore}
      header={{
        title: t('StaffRoles:Modal.AddStaffRoles'),
        description: t('StaffRoles:Modal.AddStaffRolesDescription', {
          name: contentName.toLowerCase(),
        }),
      }}
      items={staffRoles}
      searchBar={{
        value: searchText,
        onChange: setSearchText,
        selectedCount: selectedItemsSet.size,
        placeholder: t('Common:Search'),
      }}
      onClose={onClose}>
      {renderContent()}
    </ContentListModal.Main>
  );
}

export default AddStaffRolesModal;
